import stringApplySearch from "./stringApplySearch";

/**
 * Typed lowercase functionality
 */
export function toLowerCase<T extends string>(s: T): Lowercase<T> {
  return s.toLowerCase() as Lowercase<T>;
}

const acronyms = ["URL", "QR", "ID", "PDF", "DXF", "JPG", "PNG", "JPEG", "SVG", "DWG", "FBX"];

function isAcronym(str: string) {
  return str.length < 5 && acronyms.includes(str.toUpperCase());
}

export function toTitleCase(str: string | null, preserveAcronyms = true) {
  if (!str) {
    return "";
  }
  return str.replace(/\w\S*/g, (text) => {
    if (preserveAcronyms && isAcronym(text)) {
      return text.toUpperCase();
    }
    return text.charAt(0).toUpperCase() + text.substring(1).toLowerCase();
  });
}

function snakeCaseToTitleOrSentenceCase(str: string | null | undefined, titleCase: boolean, preserveAcronyms: boolean) {
  if (!str) {
    return "";
  }
  // This also has to support strings that are already in title case, so we can't just split by underscores,
  // we also have to use a regex that preserves whitespace already present in the string and separates the words.
  let wordIndex = 0;
  return str.replace(/_/g, " ").replace(/\w\S*/g, (word) => {
    ++wordIndex;
    if (preserveAcronyms && isAcronym(word)) {
      return word.toUpperCase();
    }
    if (titleCase || wordIndex === 1) {
      return word.charAt(0).toUpperCase() + word.substring(1).toLowerCase();
    }
    return word.toLowerCase();
  });
}

/**
 * Key-safe file name fragment: lowercase alphanumerics joined by dashes, capped at 40 chars.
 */
export function fileNameSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 40)
    .replace(/^-+|-+$/g, "");
}

export function snakeCaseToTitleCase(str?: string | null, preserveAcronyms = true) {
  return snakeCaseToTitleOrSentenceCase(str, true, preserveAcronyms);
}

export function snakeCaseToSentenceCase(str: string, preserveAcronyms = true) {
  return snakeCaseToTitleOrSentenceCase(str, false, preserveAcronyms);
}

export function snakeCaseToLowerSentenceCase(str: string) {
  return snakeCaseToSentenceCase(str, false).toLowerCase();
}

export function snakeCaseToUpperSentenceCase(str: string) {
  return snakeCaseToSentenceCase(str, false).toUpperCase();
}

export function snakeCaseToKebabCase(str: string) {
  if (!str) {
    return "";
  }

  return str.split("_").join("-");
}

export function kebabKaseToSnakeCase(str: string) {
  if (!str) {
    return "";
  }

  return str.split("-").join("_");
}

export function camelCaseToUpperSnakeCase(str: string) {
  if (str.match(/^[A-Z_]+$/)) {
    return str;
  }
  if (str.includes("_")) {
    return str.toUpperCase();
  }
  return str.replace(/[A-Z]/g, (match, offset) => (offset > 0 ? "_" : "") + match).toUpperCase();
}

const camelCaseRegex = /^[a-z]+([A-Z][a-z]+)+$/;

export function snakeCaseToCamelCase(str: string) {
  if (!str || camelCaseRegex.test(str)) {
    return str;
  }

  return str.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace("-", "").replace("_", ""));
}

export function snakeCaseToPascalCase(str: string) {
  return snakeCaseToCamelCase(str).replace(/^[a-z]/, (char) => char.toUpperCase());
}

function isCharNumber(c: string) {
  return c >= "0" && c <= "9";
}

function camelCaseToSentenceOrTitleCase(camelCase: string | undefined, titleCase: boolean, preserveAcronyms: boolean) {
  if (!camelCase) {
    return "";
  }

  if (typeof camelCase !== "string" || camelCase.length === 0) {
    return camelCase;
  }

  const res: string[] = [];
  let word: string[] = [];

  function addWordToResult() {
    if (res.length > 0 && res[res.length - 1] !== " ") {
      res.push(" ");
    }
    const wordString = word.join("");
    if (preserveAcronyms && isAcronym(wordString)) {
      res.push(wordString.toUpperCase());
    } else if (res.length === 0 || titleCase) {
      res.push(wordString.charAt(0).toUpperCase(), wordString.substring(1).toLowerCase());
    } else {
      res.push(wordString.toLowerCase());
    }
    word = [];
  }

  for (let i = 0, l = camelCase.length; i < l; i++) {
    // Process characters one by one, accumulating them into words first, then formatting the words, then adding the words to the result.
    const char = camelCase[i];

    if (char === " " && word.length > 0) {
      addWordToResult();
      continue;
    }

    const prevUppercase = i - 1 >= 0 && /[A-Z]/.test(camelCase[i - 1]); // Previous character is uppercase, for example "U" in "myURL"
    const nextLowercase = i + 1 < camelCase.length && /[a-z]/.test(camelCase[i + 1]); // Next character is lowercase, for example "c" in "URLSchema"

    // Check if previous character is a special character, for example "_" in "SPECIAL_CASE".
    // This is mostly done for backwards compatibility. Export debug page uses this function to format strings even though they're not in camel case.
    const prevSpecialCharacter = i - 1 >= 0 && /[\-_]/.test(camelCase[i - 1]);

    const characterStartsWord =
      (isCharNumber(char) && !isCharNumber(camelCase[i - 1])) ||
      (/[A-Z]/.test(char) && (!prevUppercase || nextLowercase) && !prevSpecialCharacter);

    if (characterStartsWord && word.length > 0) {
      addWordToResult();
    }

    word.push(char);
  }
  if (word.length > 0) {
    addWordToResult();
  }

  return res.join("");
}

export function camelCaseToTitleCase(camelCase: string | undefined, preserveAcronyms = true) {
  return camelCaseToSentenceOrTitleCase(camelCase, true, preserveAcronyms);
}

export function camelCaseToSentenceCase(camelCase: string | undefined, preserveAcronyms = true) {
  return camelCaseToSentenceOrTitleCase(camelCase, false, preserveAcronyms);
}

export function titleOrSentenceCaseToCamelCase(input: string): string {
  const s = input.trim();

  if (!s) {
    return "";
  }

  const allCapsRegex = /^[A-Z0-9]+$/;
  const titleCaseRegex = /^[A-Z][a-z0-9]+$/;
  const lowerRegex = /^[a-z0-9]+$/;

  const words = s.split(/\s+/);

  const normalizeFirst = (w: string): string =>
    allCapsRegex.test(w) || titleCaseRegex.test(w) ? w.toLowerCase() : w[0].toLowerCase() + w.slice(1);

  const normalizeNext = (w: string): string => {
    if (allCapsRegex.test(w) || titleCaseRegex.test(w) || lowerRegex.test(w)) {
      const lower = w.toLowerCase();
      return lower[0].toUpperCase() + lower.slice(1);
    }
    return w[0].toUpperCase() + w.slice(1); // keep inner caps (polyLine -> PolyLine)
  };

  const [first, ...rest] = words;
  return normalizeFirst(first) + rest.map(normalizeNext).join("");
}

export function filterStringsBySearch(strings: string[], search: string): string[] {
  if (!search) {
    return strings;
  }

  return strings.filter((str) => stringApplySearch(str, search));
}

const constantCaseRegex = /^[A-Z_]+$/;

export function toConstantCase(str: string) {
  // Don't apply transformation if it's already in constant case, as it won't work
  if (constantCaseRegex.test(str)) {
    return str;
  }

  return str
    .replace(/([A-Z]|\s([a-z]))/g, "_$1")
    .replace(/\s+/g, "")
    .toUpperCase();
}

/**
 * Converts any string to camelCase.
 */
export function toCamelCase(s: string): string {
  if (!s) {
    return "";
  }

  const pascalCase = s.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (_, character) => character.toUpperCase());

  return pascalCase.substring(0, 1).toLowerCase() + pascalCase.substring(1);
}

/**
 * Converts any string to PascalCase.
 */
export function toPascalCase(s: string): string {
  const camelCase = toCamelCase(s);
  return camelCase.substring(0, 1).toUpperCase() + camelCase.substring(1);
}

export function formatStringList(list: string[]) {
  if (!list.length) {
    return "";
  }

  const formatter = new Intl.ListFormat("en", { style: "short", type: "conjunction" });

  return formatter.format(list);
}

// ASCII printable characters - will omit non-printable input
export function isValidASCIIChar(char: string): boolean {
  const charCode = char.charCodeAt(0);
  return charCode >= 32 && charCode <= 126;
}

// A more complex approach is required to fully cover all edge cases
// Waiting on https://github.com/tc39/proposal-regex-escaping
const escapeForRegExpExpression = /[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g;
export function escapeForRegExp(str: string) {
  return str.replace(escapeForRegExpExpression, "\\$&");
}

export function isPlural(word: string) {
  return /s$|xes$|ches$|shes$|oes$|ies$/i.test(word);
}

const urlSafeRegex = /[^\w.\-]/g;

/**
 * Sanitizes a string to avoid URL-encoding issues with special characters (e.g. em dashes)
 *
 * Replaces all non-alphanumeric characters with the specified replacement character.
 * @param str - The string to sanitize.
 * @param replaceWith - The character to replace non-alphanumeric characters with. Defaults to "_".
 * @returns
 */
export function toURLSafe(str: string, replaceWith: string = "_"): string {
  return str.replace(urlSafeRegex, replaceWith);
}

/**
 * Calculates the Levenshtein distance between two strings.
 */
export function levenshteinDistance(a: string, b: string): number {
  const matrix = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) {
    matrix[i][0] = i;
  }
  for (let j = 0; j <= b.length; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      if (a[i - 1] === b[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(matrix[i - 1][j], matrix[i][j - 1], matrix[i - 1][j - 1]) + 1;
      }
    }
  }
  return matrix[a.length][b.length];
}
